import { all, takeEvery, fork, call, put } from 'redux-saga/effects';
import axios from 'axios';
import isFunction from 'lodash/isFunction';

import actions from './actions';
import { handleError } from '../../errorHandler';
import { API_HOST } from '../../constants';

const MEMBER_TICKET_API_URL = `${API_HOST}/api/memberTicket`;
const ISSUED_VOUCHER_API_URL = `${API_HOST}/api/voucher/voucherOrder/issuedVoucher`;
const VOUCHER_BOX_API_URL = `${API_HOST}/api/voucherBox`;
const MEMBER_TICKET_RECORD_API_URL = `${MEMBER_TICKET_API_URL}/record`;
const MEMBER_TICKET_REPORT_API_URL = `${MEMBER_TICKET_API_URL}/report`;
const ISSUED_MEMBER_TICKET_API_URL = `${MEMBER_TICKET_API_URL}/issuedTicket`;
const ISSUED_MEMBER_TICKET_BATCH_API_URL = `${ISSUED_MEMBER_TICKET_API_URL}/batch`;

function* createMemberTicket() {
  yield takeEvery(actions.CREATE_MEMBER_TICKET, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'post'], MEMBER_TICKET_API_URL, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.CREATE_MEMBER_TICKET_ENDED });
    }
  });
}

function* createIssuedMemberTicket() {
  yield takeEvery(actions.CREATE_ISSUED_MEMBER_TICKET, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'post'], ISSUED_MEMBER_TICKET_API_URL, formData, {
        timeout: 600000, // 10 minute
      });
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.CREATE_ISSUED_MEMBER_TICKET_ENDED });
    }
  });
}

function* allocateMemberTickets() {
  yield takeEvery(actions.ALLOCATE_MEMBER_TICKETS, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'post'], `${MEMBER_TICKET_API_URL}/allocation`, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.ALLOCATE_MEMBER_TICKETS_ENDED });
    }
  });
}

function* redeemIssuedMemberTicket() {
  yield takeEvery(actions.REDEEM_ISSUED_MEMBER_TICKET, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'post'],
        `${ISSUED_MEMBER_TICKET_API_URL}/redemption`,
        formData,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.REDEEM_ISSUED_MEMBER_TICKET_ENDED });
    }
  });
}

function* undoIssuedMemberTicketRedemption() {
  yield takeEvery(actions.UNDO_ISSUED_MEMBER_TICKET_REDEMPTION, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'post'],
        `${ISSUED_MEMBER_TICKET_API_URL}/undoRedemption`,
        formData,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.UNDO_ISSUED_MEMBER_TICKET_REDEMPTION_ENDED });
    }
  });
}

function* revokeIssuedMemberTicket() {
  yield takeEvery(actions.REVOKE_ISSUED_MEMBER_TICKET, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'post'],
        `${ISSUED_MEMBER_TICKET_API_URL}/revocation`,
        formData,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.REVOKE_ISSUED_MEMBER_TICKET_ENDED });
    }
  });
}

function* searchMemberTickets() {
  yield takeEvery(actions.SEARCH_MEMBER_TICKETS, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call([axios, 'get'], `${MEMBER_TICKET_API_URL}?${parameters.toString()}`);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.SEARCH_MEMBER_TICKETS_ENDED });
    }
  });
}

function* searchIssuedMemberTickets() {
  yield takeEvery(actions.SEARCH_ISSUED_MEMBER_TICKETS, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'get'],
        `${ISSUED_MEMBER_TICKET_API_URL}?${parameters.toString()}`,
        { timeout: 300000 }, // 5 minute
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.SEARCH_ISSUED_MEMBER_TICKETS_ENDED });
    }
  });
}

function* searchMemberTicketTransactionRecords() {
  yield takeEvery(actions.SEARCH_MEMBER_TICKET_TRANSACTION_RECORDS, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'get'],
        `${MEMBER_TICKET_RECORD_API_URL}/transaction?${parameters.toString()}`,
        { timeout: 60000 },
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.SEARCH_MEMBER_TICKET_TRANSACTION_RECORDS_ENDED });
    }
  });
}

function* searchMemberTicketTransferRecords() {
  yield takeEvery(actions.SEARCH_MEMBER_TICKET_TRANSFER_RECORDS, function* (action) {
    const { parameters, onSuccess } = action;
    try {
      const res = yield call(
        [axios, 'get'],
        `${MEMBER_TICKET_RECORD_API_URL}/transfer?${parameters.toString()}`,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.SEARCH_MEMBER_TICKET_TRANSFER_RECORDS_ENDED });
    }
  });
}

function* getIssuedMemberTicketBatches() {
  yield takeEvery(actions.GET_ISSUED_MEMBER_TICKET_BATCHES, function* (action) {
    try {
      const { parameters, onSuccess } = action;
      const res = yield call(
        [axios, 'get'],
        `${ISSUED_MEMBER_TICKET_BATCH_API_URL}?${parameters.toString()}`,
        { timeout: 300000 }, // 5 minute
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      handleError(error);
    } finally {
      yield put({ type: actions.GET_ISSUED_MEMBER_TICKET_BATCHES_ENDED });
    }
  });
}

function* findMemberTicket() {
  yield takeEvery(actions.FIND_MEMBER_TICKET, function* (action) {
    try {
      const { id, onSuccess } = action;
      const res = yield call([axios, 'get'], `${MEMBER_TICKET_API_URL}/${id}`);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.FIND_MEMBER_TICKET_ENDED });
    }
  });
}

function* updateMemberTicket() {
  yield takeEvery(actions.UPDATE_MEMBER_TICKET, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'put'], MEMBER_TICKET_API_URL, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.UPDATE_MEMBER_TICKET_ENDED });
    }
  });
}

function* updateIssuedMemberTicketBatch() {
  yield takeEvery(actions.UPDATE_ISSUED_MEMBER_TICKET_BATCH, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'put'], ISSUED_MEMBER_TICKET_BATCH_API_URL, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.UPDATE_ISSUED_MEMBER_TICKET_BATCH_ENDED });
    }
  });
}

function* updateIssuedMemberTicket() {
  yield takeEvery(actions.UPDATE_ISSUED_MEMBER_TICKET, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'put'], ISSUED_MEMBER_TICKET_API_URL, formData);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.UPDATE_ISSUED_MEMBER_TICKET_ENDED });
    }
  });
}

function* deleteMemberTickets() {
  yield takeEvery(actions.DELETE_MEMBER_TICKETS, function* (action) {
    const { formData, onSuccess } = action;
    try {
      const res = yield call([axios, 'delete'], MEMBER_TICKET_API_URL, { data: formData });
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.DELETE_MEMBER_TICKETS_ENDED });
    }
  });
}

function* getMemberTicketStatistics() {
  yield takeEvery(actions.GET_MEMBER_TICKET_STATISTICS, function* (action) {
    const { id, onSuccess } = action;
    try {
      const res = yield call([axios, 'get'], `${MEMBER_TICKET_API_URL}/${id}/statistics`);
      if (isFunction(onSuccess)) {
        yield call(onSuccess, res.data.data);
      }
    } catch (err) {
      yield handleError(err);
    } finally {
      yield put({ type: actions.GET_MEMBER_TICKET_STATISTICS_ENDED });
    }
  });
}

function* revokeVoucherMemberTicket() {
  yield takeEvery(actions.REVOKE_VOUCHER_TICKET, function* (action) {
    const { id, onSuccess } = action;
    try {
      yield call([axios, 'delete'], `${ISSUED_VOUCHER_API_URL}/${id}`);
      if (isFunction(onSuccess)) {
        yield call(onSuccess);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.REVOKE_VOUCHER_TICKET_ENDED });
    }
  });
}

function* searchVoucherWithMemberId() {
  yield takeEvery(actions.SEARCH_VOUCHER_WITH_MEMBER_ID, function* (action) {
    const { parameters, onSuccess, isMount } = action;
    try {
      const res = yield call([axios, 'get'], `${ISSUED_VOUCHER_API_URL}?${parameters.toString()}`);
      const filteredData = {
        ...res.data,
        data: res.data.data.map(
          ({
            created_at,
            id,
            no,
            redeemable_end_at,
            redeemable_start_at,
            redeemed_times,
            max_redeemable_times,
            source_mmrm_module_name,
            status,
            voucher,
            voucher_box_name,
            voucher_id,
            voucher_order_no,
          }) => {
            const { applicable_brands, name } = voucher;
            return {
              brand_name: applicable_brands.length !== 0 ? applicable_brands[0].name : null,
              created_at,
              id,
              name,
              no,
              redeemable_end_at,
              redeemable_start_at,
              redeemed_times,
              max_redeemable_times,
              source_mmrm_module_name,
              status,
              voucher_box_name,
              voucher_id,
              voucher_order_no,
            };
          },
        ),
      };
      let voucherBoxesList = [];
      if (isMount) {
        const voucherBoxParameters = new URLSearchParams({
          sort_by: 'updated_at',
          order: 'desc',
          page: 1,
          number_per_page: 100,
        });
        const { data: voucherBoxData } = yield call(
          [axios, 'get'],
          `${VOUCHER_BOX_API_URL}?${voucherBoxParameters.toString()}`,
        );
        voucherBoxesList = voucherBoxData.data.map(({ id, name }) => ({ value: id, name }));
      }
      if (isFunction(onSuccess)) {
        yield call(onSuccess, filteredData, voucherBoxesList);
      }
    } catch (error) {
      yield handleError(error);
    } finally {
      yield put({ type: actions.SEARCH_VOUCHER_WITH_MEMBER_ID_ENDED });
    }
  });
}

function* searchRedeemRecordAndTickets() {
  yield takeEvery(actions.SEARCH_REDEEM_RECORD_AND_TICKETS, function* (action) {
    const { parameters, isFirst, onSuccess } = action;
    const ticketsParameter = new URLSearchParams({
      sort_by: 'updated_at',
      order: 'desc',
      page: 1,
      number_per_page: 1000,
    });
    try {
      let ticketsData = [];
      if (isFirst) {
        const { data: tickets } = yield call(
          [axios, 'get'],
          `${MEMBER_TICKET_API_URL}?${ticketsParameter.toString()}`,
        );
        ticketsData = tickets.data.map(({ name, id }) => ({ name, id }));
      }
      const { data } = yield call(
        [axios, 'get'],
        `${MEMBER_TICKET_REPORT_API_URL}/redeemRecord?${parameters.toString()}`,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, data, ticketsData);
      }
    } catch (err) {
      yield handleError(err);
    } finally {
      yield put({
        type: actions.SEARCH_REDEEM_RECORD_AND_TICKETS_ENDED,
      });
    }
  });
}

function* searchRedeemDetailRecordAndTickets() {
  yield takeEvery(actions.SEARCH_REDEEM_RECORD_DETAILS_AND_TICKETS, function* (action) {
    const { parameters, isFirst, onSuccess } = action;
    const ticketsParameter = new URLSearchParams({
      sort_by: 'updated_at',
      order: 'desc',
      page: 1,
      number_per_page: 1000,
    });
    try {
      let ticketsData = [];
      if (isFirst) {
        const { data: tickets } = yield call(
          [axios, 'get'],
          `${MEMBER_TICKET_API_URL}?${ticketsParameter.toString()}`,
        );
        ticketsData = tickets.data.map(({ name, id }) => ({ name, id }));
      }
      const { data } = yield call(
        [axios, 'get'],
        `${MEMBER_TICKET_REPORT_API_URL}/redeemDetailRecord?${parameters.toString()}`,
      );
      if (isFunction(onSuccess)) {
        yield call(onSuccess, data, ticketsData);
      }
    } catch (err) {
      yield handleError(err);
    } finally {
      yield put({
        type: actions.SEARCH_REDEEM_RECORD_DETAILS_AND_TICKETS_ENDED,
      });
    }
  });
}

export default function* rootSaga() {
  yield all([
    fork(createMemberTicket),
    fork(createIssuedMemberTicket),
    fork(allocateMemberTickets),
    fork(redeemIssuedMemberTicket),
    fork(undoIssuedMemberTicketRedemption),
    fork(revokeIssuedMemberTicket),
    fork(searchMemberTickets),
    fork(searchIssuedMemberTickets),
    fork(searchMemberTicketTransactionRecords),
    fork(searchMemberTicketTransferRecords),
    fork(getIssuedMemberTicketBatches),
    fork(findMemberTicket),
    fork(updateMemberTicket),
    fork(updateIssuedMemberTicketBatch),
    fork(updateIssuedMemberTicket),
    fork(deleteMemberTickets),
    fork(getMemberTicketStatistics),
    fork(revokeVoucherMemberTicket),
    fork(searchVoucherWithMemberId),
    fork(searchRedeemRecordAndTickets),
    fork(searchRedeemDetailRecordAndTickets),
  ]);
}
